import { useState } from 'react';
import { auth } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import AdminLayout from '../../components/admin/AdminLayout';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';

function ManageOrders() {
  const [user] = useAuthState(auth);
  const { club } = useClub();
  
  // Use tenant-scoped data hooks
  const { data: rawOrders, loading, setData: setOrders } = useTenantCollection('orders');
  const { update, remove } = useTenantCrud('orders');
  
  // Sort orders by date (newest first)
  const orders = [...rawOrders].sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
    return dateB - dateA;
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const statusOptions = [
    { value: 'pending_payment', label: 'Pending Payment', color: '#f59e0b' },
    { value: 'paid', label: 'Paid', color: '#3b82f6' },
    { value: 'processing', label: 'Processing', color: '#8b5cf6' },
    { value: 'shipped', label: 'Shipped', color: '#06b6d4' },
    { value: 'delivered', label: 'Delivered', color: '#22c55e' },
    { value: 'cancelled', label: 'Cancelled', color: '#ef4444' }
  ];

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-TT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.color : '#9ca3af';
  };

  const getStatusLabel = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Update order status (tenant-scoped)
      await update(orderId, { status: newStatus });
      setOrders(rawOrders.map(o =>
        o.id === orderId ? { ...o, status: newStatus } : o
      ));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;

    try {
      // Delete order (tenant-scoped)
      await remove(orderId);
      setOrders(rawOrders.filter(o => o.id !== orderId));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Error deleting order:', error);
    }
  };

  // Filter orders
  const filteredOrders = filterStatus === 'all'
    ? orders
    : orders.filter(o => o.status === filterStatus);

  // Stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending_payment').length,
    paid: orders.filter(o => o.status === 'paid').length,
    processing: orders.filter(o => o.status === 'processing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    revenue: orders
      .filter(o => ['paid', 'processing', 'shipped', 'delivered'].includes(o.status))
      .reduce((sum, o) => sum + (o.total || 0), 0)
  };

  return (
    <AdminLayout title="Manage Orders" subtitle={`View and manage Kit Shop orders for ${club?.name || 'your club'}`}>

          {/* Stats */}
          <div className="orders-stats">
            <div className="order-stat-card">
              <span className="stat-number">{stats.total}</span>
              <span className="stat-label">Total Orders</span>
            </div>
            <div className="order-stat-card pending">
              <span className="stat-number">{stats.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="order-stat-card paid">
              <span className="stat-number">{stats.paid + stats.processing}</span>
              <span className="stat-label">To Process</span>
            </div>
            <div className="order-stat-card shipped">
              <span className="stat-number">{stats.shipped}</span>
              <span className="stat-label">Shipped</span>
            </div>
            <div className="order-stat-card revenue">
              <span className="stat-number">TT$ {stats.revenue.toLocaleString()}</span>
              <span className="stat-label">Revenue</span>
            </div>
          </div>

          {/* Filters */}
          <div className="admin-filters">
            <div className="filter-group">
              <label>Status:</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All Orders</option>
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-stats">
              <span className="stat-pill">{filteredOrders.length} orders</span>
            </div>
          </div>

          {/* Orders List */}
          <div className="admin-card">
            <div className="card-content">
              {loading ? (
                <p className="loading-text">Loading orders...</p>
              ) : filteredOrders.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📦</span>
                  <h3>No Orders Found</h3>
                  <p>{orders.length === 0 ? 'No orders have been placed yet.' : 'No orders match your filter.'}</p>
                </div>
              ) : (
                <div className="orders-list">
                  {filteredOrders.map(order => (
                    <div
                      key={order.id}
                      className={`order-card ${selectedOrder?.id === order.id ? 'selected' : ''}`}
                      onClick={() => setSelectedOrder(order)}
                    >
                      <div
                        className="order-status-bar"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      ></div>
                      <div className="order-main-info">
                        <h3>{order.orderId || order.id}</h3>
                        <div className="order-meta">
                          <span className="order-customer">
                            {order.customer?.firstName} {order.customer?.lastName}
                          </span>
                          <span className="order-date">{formatDate(order.createdAt)}</span>
                        </div>
                      </div>
                      <div className="order-items-count">
                        <span>{order.items?.length || 0} items</span>
                      </div>
                      <div className="order-total">
                        <span>TT$ {order.total?.toLocaleString()}</span>
                      </div>
                      <div className="order-status">
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(order.status) }}
                        >
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content modal-xlarge" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button className="modal-close" onClick={() => setSelectedOrder(null)}>×</button>
            </div>
            <div className="modal-body">
              {/* Order Status */}
              <div className="order-detail-status">
                <span className="current-status">
                  Status:
                  <span
                    className="status-badge large"
                    style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
                  >
                    {getStatusLabel(selectedOrder.status)}
                  </span>
                </span>
                <div className="status-actions">
                  {selectedOrder.status === 'paid' && (
                    <button
                      className="btn-status processing"
                      onClick={() => updateOrderStatus(selectedOrder.id, 'processing')}
                    >
                      Start Processing
                    </button>
                  )}
                  {selectedOrder.status === 'processing' && (
                    <button
                      className="btn-status shipped"
                      onClick={() => updateOrderStatus(selectedOrder.id, 'shipped')}
                    >
                      Mark Shipped
                    </button>
                  )}
                  {selectedOrder.status === 'shipped' && (
                    <button
                      className="btn-status delivered"
                      onClick={() => updateOrderStatus(selectedOrder.id, 'delivered')}
                    >
                      Mark Delivered
                    </button>
                  )}
                  {!['delivered', 'cancelled'].includes(selectedOrder.status) && (
                    <button
                      className="btn-status cancel"
                      onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                    >
                      Cancel Order
                    </button>
                  )}
                </div>
              </div>

              {/* Order Info */}
              <div className="order-detail-section">
                <h3>Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Order ID</label>
                    <span>{selectedOrder.orderId || selectedOrder.id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date</label>
                    <span>{formatDate(selectedOrder.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Method</label>
                    <span>{selectedOrder.paymentMethod || 'WiPay'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Status</label>
                    <span>{selectedOrder.status === 'pending_payment' ? 'Unpaid' : 'Paid'}</span>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="order-detail-section">
                <h3>Customer Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <span>{selectedOrder.customer?.firstName} {selectedOrder.customer?.lastName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <span>
                      <a href={`mailto:${selectedOrder.customer?.email}`}>
                        {selectedOrder.customer?.email}
                      </a>
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Phone</label>
                    <span>
                      <a href={`tel:${selectedOrder.customer?.phone}`}>
                        {selectedOrder.customer?.phone}
                      </a>
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>City</label>
                    <span>{selectedOrder.customer?.city}</span>
                  </div>
                  <div className="detail-item full">
                    <label>Address</label>
                    <span>{selectedOrder.customer?.address}</span>
                  </div>
                  {selectedOrder.customer?.notes && (
                    <div className="detail-item full">
                      <label>Notes</label>
                      <span>{selectedOrder.customer.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="order-detail-section">
                <h3>Order Items</h3>
                <div className="order-items-list">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="order-item-row">
                      <div className="item-info">
                        <span className="item-name">{item.name}</span>
                        <span className="item-size">Size: {item.size}</span>
                      </div>
                      <span className="item-price">TT$ {item.price}</span>
                    </div>
                  ))}
                  <div className="order-totals">
                    <div className="total-row">
                      <span>Subtotal</span>
                      <span>TT$ {selectedOrder.subtotal?.toLocaleString()}</span>
                    </div>
                    <div className="total-row">
                      <span>Delivery</span>
                      <span>TT$ {selectedOrder.deliveryFee}</span>
                    </div>
                    <div className="total-row final">
                      <span>Total</span>
                      <span>TT$ {selectedOrder.total?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-delete"
                onClick={() => deleteOrder(selectedOrder.id)}
              >
                Delete Order
              </button>
              <button className="btn-cancel" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageOrders;
